import React, { useState } from "react";
import { useStravaStatus } from "../../hooks/useStravaStatus";
import { StravaConnectButton, StravaIcon } from "./StravaConnectButton";
import { Modal, Button, Spinner } from "../ui";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

export function StravaAccountCard({ onStatusChange }) {
  const {
    isConnected,
    athleteId,
    updatedAt,
    loading,
    isConnecting,
    error,
    connect,
    disconnect,
    checkStatus,
  } = useStravaStatus();

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleConnect = async () => {
    try {
      await connect({
        onSuccess: () => {
          if (onStatusChange) onStatusChange(true);
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await disconnect({
        onSuccess: () => {
          setShowDisconnectModal(false);
          if (onStatusChange) onStatusChange(false);
        },
      });
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
            <StravaIcon className="w-5 h-5 text-[#FC4C02]" />
          </div>
          <div>
            <div className="h-4 w-32 bg-slate-800 rounded animate-pulse mb-1.5" />
            <div className="h-3 w-48 bg-slate-800/60 rounded animate-pulse" />
          </div>
        </div>
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-900/60 border border-slate-800 hover:border-slate-700/80 transition-all rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-[#FC4C02]/15 border border-[#FC4C02]/30 flex items-center justify-center flex-shrink-0">
              <StravaIcon className="w-6 h-6 text-[#FC4C02]" />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">Strava</h3>
                {isConnected ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Conectado
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full">
                    Não vinculado
                  </span>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-0.5">
                {isConnected
                  ? `Vinculado como Atleta #${athleteId || "Strava"}${
                      updatedAt
                        ? ` • Sincronizado em ${new Date(updatedAt).toLocaleDateString("pt-BR")}`
                        : ""
                    }`
                  : "Conecte sua conta para importar automaticamente o tempo e ritmo de suas corridas."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-center">
            {isConnected ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  title="Atualizar status"
                  onClick={checkStatus}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <StravaConnectButton
                  variant="disconnect"
                  onClick={() => setShowDisconnectModal(true)}
                />
              </div>
            ) : (
              <StravaConnectButton
                onClick={handleConnect}
                loading={isConnecting}
              />
            )}
          </div>
        </div>

        {error && (
          <div className="mt-3.5 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Modal de Confirmação para Desconectar */}
      <Modal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        title="Desconectar conta Strava"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Tem certeza que deseja desvincular sua conta Strava do UrbRace? Suas
            métricas já importadas continuarão salvas, mas você não poderá buscar
            novos treinos até reconectar.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDisconnectModal(false)}
              disabled={disconnecting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
              loading={disconnecting}
            >
              Desconectar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default StravaAccountCard;
